import { Vue } from "vue-property-decorator";
import DataService from "@/services/data-service/data-service";
import { IDBPDatabase } from "idb/build/esm/entry";
import VmsDbSchema from "@/models/VmsDbSchema";
import { EpmdTypeEnum } from "@/models/EpmdTypeEnum";
import IWasteBulkRecord from "@/models/waste-bulk-record";
import IEpmdHeader from "@/models/epmd-header";
import IShipAreaFinding from "@/models/ship-finding";

export class ActionQueueService {
  public state = Vue.observable({
    funcToRun: [] as IFuncToRun[]
  });

  public actions = {
    push: this.push.bind(this),
    undo: this.pop.bind(this),
    run: this.shift.bind(this),
    initialize: this.initialize.bind(this)
  };

  constructor(private $db: Promise<IDBPDatabase<VmsDbSchema>>, private dataService: DataService) {}
  public async initialize() {
    const storedActions = await (await this.$db).getAll("ActionsQueue");
    this.state.funcToRun = [...storedActions];
  }

  private async push(action: IFuncToRun) {
    const id = await (await this.$db).add("ActionsQueue", action);
    action.id = id;
    this.state.funcToRun.push(action);
    return id;
  }

  private async pop() {
    const action = this.state.funcToRun.pop();
    if (!action || action.id == undefined) return;
    return (await this.$db).delete("ActionsQueue", action.id);
  }

  private async shift() {
    const action = this.state.funcToRun.shift();
    let shipAreaFinding: IShipAreaFinding | null = null;
    if (!action || action.id == undefined) return;
    try {
      switch (action.functionToRun) {
        case FunctionToRunEnum.EPMDDelete:
          const { id } = action.properties as IDeleteProperties;
          await this.dataService.actions.EPMD.DeleteWasteBulkRecord(id);
          break;
        case FunctionToRunEnum.EPMDAddOrUpdate:
          const { wasteBulkRecord, labels } = action.properties as IEPMDAddOrUpdateProperties;
          await this.dataService.actions.EPMD.AddOrUpdateRecords(
            action.epmdType ?? 0,
            wasteBulkRecord,
            labels
          );
          break;
        case FunctionToRunEnum.MSCADeleteFinding:
          const { findingId, compartmentAssessmentId } = action.properties as IMSCADeleteProperties;
          await this.dataService.actions.MSCA.ShipAreasFinding.Delete(
            findingId,
            compartmentAssessmentId
          );
          break;
        case FunctionToRunEnum.MSCAAddFinding:
          shipAreaFinding = (action.properties as IShipAreaFindingProperties).shipAreaFinding;
          await this.dataService.actions.MSCA.ShipAreasFinding.Add(shipAreaFinding);
          break;
        case FunctionToRunEnum.MSCAUpdateFinding:
          shipAreaFinding = (action.properties as IShipAreaFindingProperties).shipAreaFinding;
          await this.dataService.actions.MSCA.ShipAreasFinding.Update(shipAreaFinding);
          break;
      }
      await (await this.$db).delete("ActionsQueue", action.id);
    } catch (e: any) {
      this.dataService.actions.showErrorExceptionMessage(e);
    }
    return action;
  }
}

export interface IFuncToRun {
  msg: string;
  id?: number;
  epmdType?: EpmdTypeEnum;
  moduleId: ModulesEnum;
  actionId: ActionIdEnum;
  functionToRun: FunctionToRunEnum;
  properties:
    | IDeleteProperties
    | undefined
    | IEPMDAddOrUpdateProperties
    | IShipAreaFindingProperties
    | IMSCADeleteProperties;
}

export interface IDeleteProperties {
  type: "delete";
  id: string;
}

export interface IMSCADeleteProperties {
  type: "mscaDelete";
  findingId: string;
  compartmentAssessmentId: string;
}

export interface IShipAreaFindingProperties {
  type: "shipAreaFinding";
  shipAreaFinding: IShipAreaFinding;
}

export interface IEPMDAddOrUpdateProperties {
  type: "addOrUpdate";
  wasteBulkRecord: IWasteBulkRecord;
  labels: IEpmdHeader[];
}

export enum FunctionToRunEnum {
  EPMDAddOrUpdate,
  EPMDDelete,
  MSCADeleteFinding,
  MSCAAddFinding,
  MSCAUpdateFinding
}
export enum ActionIdEnum {
  INSERT = 1,
  UPDATE,
  DELETE
}

export enum ModulesEnum {
  EPMD = 1,
  MSCA,
  RAG,
  Admin
}
